import React, { useEffect, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Modal,
  Space,
  Spin,
  Steps,
  Tag,
  Typography,
} from 'antd';
import DateHelper from 'app/helpers/dateHelper';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { getSelectedDivisionIdOrUndefined } from 'app/shared/division/selectedDivisionStorage';
import {
  getDraftMinimumStartDates,
  validateAcademicYearDatesDraft,
  type DraftMinimumStartDatesByProduct,
  type DraftPricingYearDetails,
  type PricingYearDateRange,
} from 'app/shared/api/draftPricingYear';

type DateFieldKey = 'from' | 'to';
type CategoryKey = 'accommodation' | 'courses' | 'addOns' | 'packages' | 'transfers';

type RangeValue = {
  from: Dayjs | null;
  to: Dayjs | null;
};

type StepOneState = Record<CategoryKey, RangeValue>;

type SerialisedRange = {
  from: string | null;
  to: string | null;
};

type SerialisedCategoryState = {
  countries: Record<string, SerialisedRange>;
  centres: Record<string, Record<string, SerialisedRange>>;
};

type WizardStatePayload = {
  pricingYear?: number;
  stepOneDates?: Record<string, SerialisedRange>;
  highSeason?: Record<string, SerialisedCategoryState>;
};

const { Title, Paragraph, Text } = Typography;

const readSelectedDivisionId = (): number | undefined => getSelectedDivisionIdOrUndefined();

const categories: Array<{ key: CategoryKey; label: string }> = [
  { key: 'accommodation', label: 'Accommodation Dates' },
  { key: 'courses', label: 'Course Dates' },
  { key: 'addOns', label: 'Add Ons Dates' },
  { key: 'packages', label: 'Package Dates' },
  { key: 'transfers', label: 'Transfer Dates' },
];

const defaultMinimumStartDateFrom = (pricingYear: number): Dayjs =>
  dayjs(`${pricingYear - 1}-12-31`);

const resolveCategoryMinimumFrom = (
  pricingYear: number,
  key: CategoryKey,
  minimumDates?: DraftMinimumStartDatesByProduct | null
): Dayjs => {
  const fallback = defaultMinimumStartDateFrom(pricingYear);
  const iso = minimumDates?.[key];
  const parsed = iso ? dayjs(iso.slice(0, 10)) : null;
  return parsed?.isValid() ? parsed : fallback;
};

const buildCategoryMinDates = (
  pricingYear: number,
  minimumDates?: DraftMinimumStartDatesByProduct | null
): Record<CategoryKey, Dayjs> =>
  categories.reduce((acc, { key }) => {
    acc[key] = resolveCategoryMinimumFrom(pricingYear, key, minimumDates);
    return acc;
  }, {} as Record<CategoryKey, Dayjs>);

/** Earliest date for the help text: min(31-Dec prior year, all API minimum start dates). */
const resolveDisplayRangeStart = (
  pricingYear: number,
  minimumDates?: DraftMinimumStartDatesByProduct | null
): Dayjs => {
  let earliest = defaultMinimumStartDateFrom(pricingYear);
  if (!minimumDates) return earliest;

  categories.forEach(({ key }) => {
    const iso = minimumDates[key];
    if (!iso) return;
    const parsed = dayjs(iso.slice(0, 10));
    if (parsed.isValid() && parsed.isBefore(earliest, 'day')) {
      earliest = parsed;
    }
  });

  return earliest;
};

const buildStepOneStateFromMinimumDates = (
  pricingYear: number,
  minimumDates?: DraftMinimumStartDatesByProduct | null
): StepOneState => {
  const yearEnd = dayjs(`${pricingYear}-12-31`);

  return categories.reduce((acc, { key }) => {
    acc[key] = {
      from: resolveCategoryMinimumFrom(pricingYear, key, minimumDates),
      to: yearEnd,
    };
    return acc;
  }, {} as StepOneState);
};

const parseStepOneDatesFromNavigation = (
  stored: Record<string, SerialisedRange> | undefined
): StepOneState =>
  categories.reduce((acc, { key }) => {
    const range = stored?.[key];
    acc[key] = {
      from: range?.from ? dayjs(range.from) : null,
      to: range?.to ? dayjs(range.to) : null,
    };
    return acc;
  }, {} as StepOneState);

const parseDDMMYYYY = (value: string): Dayjs | null => {
  const trimmed = value.replace(/\s/g, '');
  if (!/^\d{8}$/.test(trimmed)) return null;
  const parsed = dayjs(trimmed, 'DDMMYYYY', true);
  return parsed.isValid() ? parsed : null;
};

const validateRange = (range: RangeValue, minDate: Dayjs, maxDate: Dayjs): string | undefined => {
  const { from, to } = range;
  if (!from || !to) {
    return 'Select both dates to continue.';
  }
  if (!to.isAfter(from, 'day')) {
    return '"Date To" must be later than "Date From".';
  }
  if (
    from.isBefore(minDate, 'day') ||
    from.isAfter(maxDate, 'day') ||
    to.isBefore(minDate, 'day') ||
    to.isAfter(maxDate, 'day')
  ) {
    return `Dates must fall between ${minDate.format('DD-MMM-YYYY')} and ${maxDate.format('DD-MMM-YYYY')}.`;
  }
  return undefined;
};

const serialiseDates = (state: StepOneState): Record<string, SerialisedRange> => {
  return Object.entries(state).reduce((acc, [key, range]) => {
    acc[key] = {
      from: range.from?.toISOString() ?? null,
      to: range.to?.toISOString() ?? null,
    };
    return acc;
  }, {} as Record<string, SerialisedRange>);
};

const Wizard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState: WizardStatePayload = (location.state as WizardStatePayload | undefined) ?? {};

  const pricingYear = locationState.pricingYear ?? new Date().getFullYear() + 1;
  const divisionId = useMemo(() => readSelectedDivisionId(), []);
  const restoredFromNavigation = Boolean(locationState.stepOneDates);

  const [dateState, setDateState] = useState<StepOneState>(() => {
    if (locationState.stepOneDates) {
      return parseStepOneDatesFromNavigation(locationState.stepOneDates);
    }
    return buildStepOneStateFromMinimumDates(pricingYear, null);
  });

  const [categoryMinDates, setCategoryMinDates] = useState<Record<CategoryKey, Dayjs>>(
    () => buildCategoryMinDates(pricingYear, null)
  );

  const [minimumDatesFromApi, setMinimumDatesFromApi] =
    useState<DraftMinimumStartDatesByProduct | null>(null);

  const [datesLoading, setDatesLoading] = useState(
    () => !restoredFromNavigation && Boolean(divisionId)
  );

  const [inlineErrors, setInlineErrors] = useState<Partial<Record<CategoryKey, { from?: string; to?: string }>>>({});
  const [validating, setValidating] = useState(false);

  useEffect(() => {
    if (!divisionId) {
      setMinimumDatesFromApi(null);
      setCategoryMinDates(buildCategoryMinDates(pricingYear, null));
      if (!restoredFromNavigation) {
        setDateState(buildStepOneStateFromMinimumDates(pricingYear, null));
      }
      setDatesLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      if (!restoredFromNavigation) {
        setDatesLoading(true);
      }
      try {
        const minimumDates = await getDraftMinimumStartDates(
          divisionId,
          pricingYear - 1
        );
        if (!cancelled) {
          setMinimumDatesFromApi(minimumDates);
          setCategoryMinDates(buildCategoryMinDates(pricingYear, minimumDates));
          if (!restoredFromNavigation) {
            setDateState(buildStepOneStateFromMinimumDates(pricingYear, minimumDates));
          }
        }
      } catch {
        if (!cancelled) {
          setMinimumDatesFromApi(null);
          setCategoryMinDates(buildCategoryMinDates(pricingYear, null));
          if (!restoredFromNavigation) {
            setDateState(buildStepOneStateFromMinimumDates(pricingYear, null));
          }
        }
      } finally {
        if (!cancelled && !restoredFromNavigation) {
          setDatesLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [divisionId, pricingYear, restoredFromNavigation]);

  const yearStart = useMemo(() => dayjs(`${pricingYear}-01-01`), [pricingYear]);
  const yearEnd = useMemo(() => yearStart.endOf('year'), [yearStart]);
  const maxDate = useMemo(() => yearEnd.add(7, 'day'), [yearEnd]);
  const displayRangeStart = useMemo(
    () => resolveDisplayRangeStart(pricingYear, minimumDatesFromApi),
    [pricingYear, minimumDatesFromApi]
  );

  const tryApplyDDMMYYYY = (category: CategoryKey, field: DateFieldKey, rawValue: string) => {
    const parsed = parseDDMMYYYY(rawValue);
    if (parsed) {
      setInlineErrors((prev) => {
        const next = { ...prev };
        if (!next[category]) next[category] = {};
        next[category] = { ...next[category], [field]: undefined };
        return next;
      });
      handleDateChange(category, field, parsed);
    } else {
      setInlineErrors((prev) => ({
        ...prev,
        [category]: { ...prev[category], [field]: 'Invalid date.' },
      }));
    }
  };

  const handleDateFieldBlur = (category: CategoryKey, field: DateFieldKey) => (e: React.FocusEvent<HTMLInputElement>) => {
    const value = (e.target as HTMLInputElement)?.value?.replace(/\s/g, '') ?? '';
    if (/^\d{8}$/.test(value)) {
      tryApplyDDMMYYYY(category, field, value);
    }
  };

  const handleDateFieldKeyDown = (category: CategoryKey, field: DateFieldKey) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      const target = e.target as HTMLInputElement;
      const value = target?.value?.replace(/\s/g, '') ?? '';
      if (/^\d{8}$/.test(value)) {
        e.preventDefault();
        tryApplyDDMMYYYY(category, field, value);
      }
    }
  };

  const handleDateChange = (category: CategoryKey, field: DateFieldKey, value: Dayjs | null) => {
    setDateState((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value,
      },
    }));
  };

  const makeDisabledFromDate = (key: CategoryKey) => (current: Dayjs) => {
    if (!current) return false;
    const categoryMin = categoryMinDates[key];
    if (current.isBefore(categoryMin, 'day') || current.isAfter(maxDate, 'day')) return true;
    const to = dateState[key].to;
    if (to && current.isAfter(to, 'day')) return true;
    return false;
  };

  const makeDisabledToDate = (key: CategoryKey) => (current: Dayjs) => {
    if (!current) return false;
    const categoryMin = categoryMinDates[key];
    const from = dateState[key].from;
    const effectiveMin =
      from && from.isAfter(categoryMin, 'day') ? from : categoryMin;
    if (current.isBefore(effectiveMin, 'day') || current.isAfter(maxDate, 'day')) return true;
    return false;
  };

  const handleBack = () => {
    navigate('/new-pricing-year');
  };

  const buildValidationPayload = (): DraftPricingYearDetails | null => {
    if (!divisionId) return null;

    const toPayloadRange = (key: CategoryKey): PricingYearDateRange | null => {
      const { from, to } = dateState[key];
      if (!from || !to) return null;
      return {
        dateFrom: from.format('YYYY-MM-DD'),
        dateTo: to.format('YYYY-MM-DD'),
      };
    };

    return {
      divisionId,
      year: pricingYear,
      academicYearDates: {
        courses: toPayloadRange('courses'),
        accommodation: toPayloadRange('accommodation'),
        addOns: toPayloadRange('addOns'),
        transfers: toPayloadRange('transfers'),
        packages: toPayloadRange('packages'),
      },
      highSeasonDates: {
        accommodation: [],
        courses: [],
        addOns: [],
        transfers: [],
        packages: [],
      },
    };
  };

  const showValidationErrors = (messages: string[]) => {
    Modal.warning({
      title: 'Check academic dates',
      content: (
        <ul style={{ marginBottom: 0, paddingLeft: 18 }}>
          {messages.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ul>
      ),
    });
  };

  const extractApiErrorMessages = (err: unknown): string[] => {
    const errorMessages = (err as { errorMessages?: string[] })?.errorMessages;
    if (Array.isArray(errorMessages) && errorMessages.length > 0) {
      const parsed = errorMessages
        .map((msg) => {
          if (typeof msg !== 'string') return String(msg);
          try {
            const json = JSON.parse(msg);
            if (Array.isArray(json) && json[0]?.message) {
              return String(json[0].message);
            }
            if (json?.message) {
              return String(json.message);
            }
          } catch {
            // keep raw message
          }
          return msg;
        })
        .filter((msg) => msg && msg.trim());

      return Array.from(new Set(parsed));
    }

    const text = err instanceof Error ? err.message : 'Failed to validate academic year dates.';
    return text ? [text] : ['Failed to validate academic year dates.'];
  };

  const handleNext = async () => {
    const issues: string[] = [];

    categories.forEach(({ key, label }) => {
      const range = dateState[key];
      const categoryMin = categoryMinDates[key];
      if (!range.from || !range.to) {
        issues.push(`${label}: select both From and To dates.`);
        return;
      }
      if (!range.to.isAfter(range.from, 'day')) {
        issues.push(`${label}: "Date To" must be later than "Date From".`);
        return;
      }
      if (
        range.from.isBefore(categoryMin, 'day') ||
        range.from.isAfter(maxDate, 'day') ||
        range.to.isBefore(categoryMin, 'day') ||
        range.to.isAfter(maxDate, 'day')
      ) {
        issues.push(
          `${label}: dates must fall between ${categoryMin.format('DD-MMM-YYYY')} and ${maxDate.format('DD-MMM-YYYY')}.`
        );
      }
    });

    if (issues.length) {
      showValidationErrors(issues);
      return;
    }

    if (!divisionId) {
      Modal.warning({
        title: 'No division selected',
        content: 'Choose a division from the header before continuing.',
      });
      return;
    }

    const payload = buildValidationPayload();
    if (!payload) return;

    setValidating(true);
    try {
      await validateAcademicYearDatesDraft(divisionId, payload);
      navigate('/new-pricing-year/wizard/step-2', {
        state: {
          pricingYear,
          dates: serialiseDates(dateState),
          highSeason: locationState.highSeason,
        },
      });
    } catch (err) {
      showValidationErrors(extractApiErrorMessages(err));
    } finally {
      setValidating(false);
    }
  };

  const renderCategoryCard = (key: CategoryKey, label: string) => {
    const range = dateState[key];
    const categoryMin = categoryMinDates[key];
    const error =
      range.from && range.to ? validateRange(range, categoryMin, maxDate) : undefined;
    const fromError = inlineErrors[key]?.from;
    const toError = inlineErrors[key]?.to;

    return (
      <Card key={key} size="small" style={{ minWidth: 260 }}>
        <Spin spinning={datesLoading}>
        <Space orientation="vertical" size={12} style={{ width: '100%' }}>
          <Text strong>{label}</Text>
          <Space size={16} wrap>
            <Space orientation="vertical" size={4}>
              <Text type="secondary">From</Text>
              <DatePicker
                value={range.from}
                onChange={(val) => handleDateChange(key, 'from', val)}
                onBlur={handleDateFieldBlur(key, 'from')}
                onKeyDown={handleDateFieldKeyDown(key, 'from')}
                format={DateHelper.DATE_PICKER_FORMATS}
                allowClear
                disabled={datesLoading}
                disabledDate={makeDisabledFromDate(key)}
                defaultPickerValue={range.from ?? categoryMin}
                style={{ width: 160 }}
              />
              {fromError && <Text type="danger">{fromError}</Text>}
            </Space>
            <Space orientation="vertical" size={4}>
              <Text type="secondary">To</Text>
              <DatePicker
                value={range.to}
                onChange={(val) => handleDateChange(key, 'to', val)}
                onBlur={handleDateFieldBlur(key, 'to')}
                onKeyDown={handleDateFieldKeyDown(key, 'to')}
                format={DateHelper.DATE_PICKER_FORMATS}
                allowClear
                disabled={datesLoading}
                disabledDate={makeDisabledToDate(key)}
                defaultPickerValue={range.to ?? range.from ?? categoryMin}
                style={{ width: 160 }}
              />
              {toError && <Text type="danger">{toError}</Text>}
            </Space>
          </Space>
          {range.from && !range.to && <Text type="danger">Select both dates to continue.</Text>}
          {range.to && !range.from && <Text type="danger">Select both dates to continue.</Text>}
          {error && <Text type="danger">{error}</Text>}
        </Space>
        </Spin>
      </Card>
    );
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 16px' }}>
      <div style={{ width: '100%', maxWidth: 960 }}>
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={handleBack}
          style={{ paddingLeft: 0, marginBottom: 8 }}
        >
          Back
        </Button>

        <Space align="center" size={16} style={{ marginBottom: 16 }}>
          <Title level={2} style={{ margin: 0 }}>
            New Pricing Year <Text>{pricingYear}</Text>
          </Title>
          <Tag color="processing">Draft</Tag>
        </Space>

        <Steps
          current={0}
          style={{ marginBottom: 32 }}
          items={[
            { title: 'Pick Academic Year Dates' },
            { title: 'High Season Dates' },
          ]}
        />

        <Card styles={{ body: { padding: 32 } }}>
            <Space orientation="vertical" size={24} style={{ width: '100%' }}>
              <div>
                <Title level={4} style={{ marginBottom: 12 }}>
                  Step 1: Pick Academic Year Dates
                </Title>
                <Paragraph style={{ marginBottom: 0 }}>
                  Provide start and end dates for each product area. All dates must fall between{' '}
                  {displayRangeStart.format('DD-MMM-YYYY')} to {yearEnd.format('DD-MMM-YYYY')}.
                </Paragraph>
                {!divisionId && (
                  <Alert
                    style={{ marginTop: 16 }}
                    type="warning"
                    showIcon
                    title="No division selected"
                    description="Choose a division from the header so the wizard can scope the pricing year correctly."
                  />
                )}
              </div>

              <Space wrap size={24}>
                {categories.map(({ key, label }) => renderCategoryCard(key, label))}
              </Space>

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  type="primary"
                  size="large"
                  onClick={handleNext}
                  loading={datesLoading || validating}
                  disabled={!divisionId}
                >
                  Next
                </Button>
              </div>
            </Space>
        </Card>
      </div>
    </div>
  );
};

export default Wizard;
